import { userRequest } from '../requestMethods';

class ClientLogger {
    constructor() {
        this.logs = [];
        this.performanceMetrics = [];
        this.userActions = [];
        this.errors = [];
        this.maxBatchSize = 50;
        this.flushInterval = 30000; // 30 seconds
        this.isProduction = process.env.NODE_ENV === 'production';
        
        // Initialize auto-flush
        this.startAutoFlush();
        
        // Setup global error listeners
        this.setupGlobalErrorHandlers();
        
        // Setup performance monitoring
        this.setupPerformanceMonitoring();
        
        // Flush logs before page unload
        this.setupPageUnloadHandler();
    }

    // Core logging methods
    debug(message, meta = {}) {
        this.addLog('debug', message, meta);
    }

    info(message, meta = {}) {
        this.addLog('info', message, meta);
    }

    warn(message, meta = {}) {
        this.addLog('warn', message, meta);
    }

    error(message, meta = {}) {
        this.addLog('error', message, meta);
    }

    // Specialized logging methods
    userAction(type, target, meta = {}) {
        const action = {
            type,
            target,
            meta: {
                ...meta,
                url: window.location.href,
                timestamp: new Date().toISOString()
            },
            timestamp: new Date().toISOString()
        };
        
        this.userActions.push(action);
        this.checkAndFlushActions();
    }

    performance(name, value, unit = 'ms', meta = {}) {
        const metric = {
            name,
            value: Number(value),
            unit,
            meta: {
                ...meta,
                url: window.location.href,
                timestamp: new Date().toISOString()
            }
        };
        
        this.performanceMetrics.push(metric);
        this.checkAndFlushPerformance();
    }

    reportError(error, meta = {}) {
        const errorReport = {
            message: error.message || 'Unknown error',
            stack: error.stack,
            meta: {
                ...meta,
                url: window.location.href,
                userAgent: navigator.userAgent,
                timestamp: new Date().toISOString()
            },
            timestamp: new Date().toISOString(),
            url: window.location.href,
            lineNumber: error.lineno,
            colNumber: error.colno
        };
        
        this.errors.push(errorReport);
        this.checkAndFlushErrors();
    }

    // Network request logging
    logApiRequest(config, requestId) {
        this.info('API Request Started', {
            method: config.method?.toUpperCase(),
            url: config.url,
            requestId,
            hasData: !!config.data
        });
    }

    logApiResponse(response, duration, requestId) {
        const level = response.status >= 400 ? 'warn' : 'info';
        this[level]('API Response Received', {
            status: response.status,
            statusText: response.statusText,
            url: response.config?.url,
            duration: `${duration}ms`,
            requestId,
            responseSize: response.data ? JSON.stringify(response.data).length : 0
        });
    }

    logApiError(error, duration, requestId) {
        this.error('API Request Failed', {
            message: error.message,
            status: error.response?.status,
            statusText: error.response?.statusText,
            url: error.config?.url,
            duration: duration ? `${duration}ms` : 'unknown',
            requestId
        });
    }

    // Private methods
    addLog(level, message, meta = {}) {
        const logEntry = {
            level,
            message,
            meta: {
                ...meta,
                url: window.location.href,
                userAgent: navigator.userAgent,
                userId: this.getUserId()
            },
            timestamp: new Date().toISOString()
        };

        // In development, also log to console
        if (!this.isProduction) {
            console[level] || console.log(`[${level.toUpperCase()}]`, message, meta);
        }

        this.logs.push(logEntry);
        this.checkAndFlushLogs();
    }

    getUserId() {
        // Try to get user ID from various sources
        try {
            // From Redux store if available
            const state = window.__REDUX_STORE__?.getState?.();
            if (state?.auth?.user?._id) {
                return state.auth.user._id;
            }
            
            // From localStorage (this is how authService stores user data)
            const userStr = localStorage.getItem('user');
            if (userStr) {
                const user = JSON.parse(userStr);
                return user._id;
            }
        } catch (e) {
            // Silently fail
        }
        return null;
    }

    checkAndFlushLogs() {
        if (this.logs.length >= this.maxBatchSize) {
            this.flushLogs();
        }
    }

    checkAndFlushActions() {
        if (this.userActions.length >= 20) {
            this.flushUserActions();
        }
    }

    checkAndFlushPerformance() {
        if (this.performanceMetrics.length >= 10) {
            this.flushPerformanceMetrics();
        }
    }

    checkAndFlushErrors() {
        if (this.errors.length >= 5) {
            this.flushErrors();
        }
    }

    async flushLogs() {
        if (this.logs.length === 0) return;

        const logsToSend = [...this.logs];
        this.logs = [];

        try {
            await userRequest.post('/logs/client', {
                logs: logsToSend
            });
        } catch (error) {
            // If sending logs fails, put them back (but limit to prevent infinite growth)
            this.logs.unshift(...logsToSend.slice(-10));
            
            // Only log to console in development to avoid infinite loops
            if (!this.isProduction) {
                console.error('Failed to send logs to server:', error);
            }
        }
    }

    async flushUserActions() {
        if (this.userActions.length === 0) return;

        const actionsToSend = [...this.userActions];
        this.userActions = [];

        try {
            await userRequest.post('/logs/actions', {
                actions: actionsToSend
            });
        } catch (error) {
            // Put back a limited number of actions
            this.userActions.unshift(...actionsToSend.slice(-5));
        }
    }

    async flushPerformanceMetrics() {
        if (this.performanceMetrics.length === 0) return;

        const metricsToSend = [...this.performanceMetrics];
        this.performanceMetrics = [];

        try {
            await userRequest.post('/logs/performance', {
                metrics: metricsToSend
            });
        } catch (error) {
            // Put back a limited number of metrics
            this.performanceMetrics.unshift(...metricsToSend.slice(-5));
        }
    }

    async flushErrors() {
        if (this.errors.length === 0) return;

        const errorsToSend = [...this.errors];
        this.errors = [];

        try {
            await userRequest.post('/logs/errors', {
                errors: errorsToSend
            });
        } catch (error) {
            // Put back a limited number of errors
            this.errors.unshift(...errorsToSend.slice(-3));
        }
    }

    async flushAll() {
        await Promise.all([
            this.flushLogs(),
            this.flushUserActions(),
            this.flushPerformanceMetrics(),
            this.flushErrors()
        ]);
    }

    startAutoFlush() {
        setInterval(() => {
            this.flushAll();
        }, this.flushInterval);
    }

    setupGlobalErrorHandlers() {
        // Catch JavaScript errors
        window.addEventListener('error', (event) => {
            this.reportError({
                message: event.message,
                stack: event.error?.stack,
                lineno: event.lineno,
                colno: event.colno,
                filename: event.filename
            }, {
                type: 'javascript-error',
                source: 'window.error'
            });
        });

        // Catch unhandled promise rejections
        window.addEventListener('unhandledrejection', (event) => {
            this.reportError({
                message: `Unhandled Promise Rejection: ${event.reason}`,
                stack: event.reason?.stack
            }, {
                type: 'unhandled-promise-rejection',
                source: 'window.unhandledrejection'
            });
        });
    }

    setupPerformanceMonitoring() {
        // Monitor page load performance
        window.addEventListener('load', () => {
            // Use setTimeout to ensure performance entries are available
            setTimeout(() => {
                if (window.performance && window.performance.timing) {
                    const timing = window.performance.timing;
                    const navigation = window.performance.navigation;
                    
                    this.performance('page-load-total', timing.loadEventEnd - timing.navigationStart);
                    this.performance('dom-content-loaded', timing.domContentLoadedEventEnd - timing.navigationStart);
                    this.performance('dom-interactive', timing.domInteractive - timing.navigationStart);
                    
                    this.info('Page Load Complete', {
                        navigationType: navigation.type,
                        redirectCount: navigation.redirectCount,
                        url: window.location.href
                    });
                }
            }, 100);
        });

        // Monitor route changes (for SPAs)
        let currentPath = window.location.pathname;
        const checkRouteChange = () => {
            if (window.location.pathname !== currentPath) {
                this.userAction('navigate', window.location.pathname, {
                    from: currentPath,
                    to: window.location.pathname
                });
                currentPath = window.location.pathname;
            }
        };

        // Check for route changes periodically
        setInterval(checkRouteChange, 1000);
    }

    setupPageUnloadHandler() {
        const handleBeforeUnload = () => {
            // Try to flush remaining logs synchronously
            // Use sendBeacon with relative URL to avoid CORS issues
            if (this.logs.length > 0) {
                navigator.sendBeacon?.('/logs/client', JSON.stringify({
                    logs: this.logs
                }));
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        window.addEventListener('pagehide', handleBeforeUnload);
    }

    // Public utility methods
    startTimer(name) {
        const startTime = performance.now();
        return {
            end: (meta = {}) => {
                const duration = performance.now() - startTime;
                this.performance(name, duration, 'ms', meta);
                return duration;
            }
        };
    }

    logComponentMount(componentName, props = {}) {
        this.info(`Component Mounted: ${componentName}`, {
            component: componentName,
            props: Object.keys(props)
        });
    }

    logComponentUnmount(componentName) {
        this.info(`Component Unmounted: ${componentName}`, {
            component: componentName
        });
    }
}

// Create singleton instance
const logger = new ClientLogger();

export default logger; 